import { Text } from "@solomon-labs/design/components/ui";
import type React from "react";
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from "react";
import { type ActiveDotProps, usePlotArea } from "recharts";

/**
 * Inline inspection — the standard hover for Solomon time-series charts
 * (Recharts). Policy in `chartInspection.intent.md`.
 *
 * Instead of a floating tooltip, the selected observation's values read out
 * in a reserved band above the plot and its date below it, both following
 * Recharts' own selection and clamped to the plot. While inspecting, the
 * context (series, grid, Y axis) dims to 22% and the X-axis labels hide; the
 * cursor and the square selected-point markers stay fully opaque.
 *
 * Wiring, for any Recharts cartesian chart:
 *
 *   const inspection = useChartInspection();
 *   <div {...inspection.containerProps} className="…">
 *       <InlineInspectionStyle />
 *       <InlineReadoutSpacer rows={n} />
 *       <ResponsiveContainer>
 *           <LineChart style={INLINE_CHART_STYLE}>
 *               <Tooltip {...inspection.tooltipProps} content={(p) => (
 *                   <InlineReadout active={Boolean(p.active)} x={p.coordinate?.x}
 *                       rows={…} date={…} onActiveChange={inspection.setInspecting} />
 *               )} />
 *               <Line activeDot={squareActiveDot(color)} />
 */

/** Height of one readout row, and so of the band reserved per row. */
export const READOUT_ROW_HEIGHT = 32;

/** Vertical page scroll keeps working when a touch starts on the chart. */
export const INLINE_CHART_STYLE: React.CSSProperties = { touchAction: "pan-y" };

// Only context dims: active dots and the cursor are separate Recharts layers.
const INLINE_INSPECTION_CSS = `
[data-inline-chart="true"] .recharts-area-area,
[data-inline-chart="true"] .recharts-area-curve,
[data-inline-chart="true"] .recharts-line-curve,
[data-inline-chart="true"] .recharts-cartesian-grid,
[data-inline-chart="true"] .recharts-yAxis,
[data-inline-chart="true"] .recharts-yAxis-tick-labels,
[data-inline-chart="true"] .recharts-xAxis-tick-labels,
[data-inline-chart="true"] .recharts-xAxis {
    transition: opacity 120ms cubic-bezier(0.4, 0, 0.2, 1);
}
[data-inline-chart="true"][data-inspecting="true"] .recharts-area-area,
[data-inline-chart="true"][data-inspecting="true"] .recharts-area-curve,
[data-inline-chart="true"][data-inspecting="true"] .recharts-line-curve,
[data-inline-chart="true"][data-inspecting="true"] .recharts-cartesian-grid,
[data-inline-chart="true"][data-inspecting="true"] .recharts-yAxis,
[data-inline-chart="true"][data-inspecting="true"] .recharts-yAxis-tick-labels { opacity: 0.22; }
[data-inline-chart="true"][data-inspecting="true"] .recharts-xAxis,
[data-inline-chart="true"][data-inspecting="true"] .recharts-xAxis-tick-labels { opacity: 0; }
[data-inline-chart="true"] .recharts-surface:focus:not(:focus-visible) { outline: none; }
@media (prefers-reduced-motion: reduce) {
    [data-inline-chart="true"] * { transition: none; }
}
`;

/** The dim/hide rules. Render once inside each inline chart's container. */
export function InlineInspectionStyle(): React.ReactElement {
    return (
        <style dangerouslySetInnerHTML={{ __html: INLINE_INSPECTION_CSS }} />
    );
}

/** Reserves the readout band above the plot, so the plot never moves. */
export function InlineReadoutSpacer({
    rows
}: {
    rows: number;
}): React.ReactElement {
    return <div style={{ height: readoutHeight(rows) }} aria-hidden />;
}

export function readoutHeight(rows: number): number {
    return Math.max(1, rows) * READOUT_ROW_HEIGHT;
}

export interface ChartInspection {
    /** Spread on the element that wraps the chart. */
    containerProps: {
        "data-inline-chart": true;
        "data-inspecting": boolean;
        onPointerMove: () => void;
        onPointerDown: () => void;
        onPointerCancel: () => void;
        onFocusCapture: () => void;
        onBlurCapture: () => void;
        onKeyDownCapture: (event: React.KeyboardEvent) => void;
    };
    /** Spread on the Recharts `<Tooltip>`; supply your own `content`. */
    tooltipProps: {
        active: false | undefined;
        position: { x: number; y: number };
        allowEscapeViewBox: { x: boolean; y: boolean };
        isAnimationActive: false;
        cursor: {
            stroke: string;
            strokeWidth: number;
            strokeDasharray: string;
        };
    };
    /** Pass to `InlineReadout`'s `onActiveChange`. */
    setInspecting: (inspecting: boolean) => void;
}

/**
 * Inspection state: Escape and blur dismiss; pointer movement, a press,
 * focus and the arrow keys resume.
 */
export function useChartInspection(): ChartInspection {
    const [inspecting, setInspectingState] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const setInspecting = useCallback(
        (next: boolean) => setInspectingState(next),
        []
    );
    const resume = useCallback(() => setDismissed(false), []);
    const dismiss = useCallback(() => setDismissed(true), []);
    return {
        containerProps: {
            "data-inline-chart": true,
            "data-inspecting": inspecting && !dismissed,
            onPointerMove: resume,
            onPointerDown: resume,
            onPointerCancel: dismiss,
            onFocusCapture: resume,
            onBlurCapture: dismiss,
            onKeyDownCapture: (event): void => {
                if (event.key === "Escape") setDismissed(true);
                else if (event.key.startsWith("Arrow")) setDismissed(false);
            }
        },
        tooltipProps: {
            active: dismissed ? false : undefined,
            position: { x: 0, y: 0 },
            allowEscapeViewBox: { x: true, y: true },
            isAnimationActive: false,
            cursor: {
                stroke: "rgb(var(--color-foreground-base))",
                strokeWidth: 0.5,
                strokeDasharray: "3 3"
            }
        },
        setInspecting
    };
}

/** The selected point: an 8px square in the series colour, never dimmed. */
export function squareActiveDot(
    color: string
): (props: ActiveDotProps) => React.ReactElement | null {
    return function SquareActiveDot(
        props: ActiveDotProps
    ): React.ReactElement | null {
        if (props.cx === undefined || props.cy === undefined) return null;
        return (
            <rect
                x={props.cx - 4}
                y={props.cy - 4}
                width={8}
                height={8}
                fill={color}
                stroke="none"
            />
        );
    };
}

export interface InlineReadoutRow {
    key: string;
    /** Shown with a swatch only when the readout has more than one row. */
    name: string;
    /** Swatch colour; omit for a row with no series of its own (a total). */
    color?: string;
    /** Already formatted; `null` renders `—` (an observed zero stays zero). */
    value: string | null;
}

interface InlineReadoutProps {
    active: boolean;
    /** Recharts' cursor x (`props.coordinate?.x`). */
    x?: number;
    rows: readonly InlineReadoutRow[];
    /** Formatted date for the selected observation. */
    date: string | null;
    onActiveChange: (active: boolean) => void;
}

/**
 * The readout itself, rendered as the Tooltip `content`. Uses Recharts' own
 * selection and plot bounds; never reconstructs the data scale.
 */
export function InlineReadout({
    active,
    x,
    rows,
    date,
    onActiveChange
}: InlineReadoutProps): React.ReactElement | null {
    const plot = usePlotArea();
    const valuesRef = useRef<HTMLDivElement>(null);
    const dateRef = useRef<HTMLSpanElement>(null);
    const [widths, setWidths] = useState({ values: 0, date: 0 });
    const visible =
        active && date !== null && x !== undefined && rows.length > 0;
    useEffect(() => {
        onActiveChange(visible);
        return (): void => onActiveChange(false);
    }, [visible, onActiveChange]);
    useLayoutEffect(() => {
        const measure = (): void => {
            const values =
                valuesRef.current?.getBoundingClientRect().width ?? 0;
            const dateWidth =
                dateRef.current?.getBoundingClientRect().width ?? 0;
            setWidths((previous) =>
                previous.values === values && previous.date === dateWidth
                    ? previous
                    : { values, date: dateWidth }
            );
        };
        measure();
        const observer = new ResizeObserver(measure);
        if (valuesRef.current) observer.observe(valuesRef.current);
        if (dateRef.current) observer.observe(dateRef.current);
        return (): void => observer.disconnect();
    }, [visible, rows, date, plot?.width]);
    if (!visible || x === undefined || !plot) return null;
    const left = (width: number): number =>
        Math.max(plot.x, Math.min(x - width / 2, plot.x + plot.width - width));
    const named = rows.length > 1;
    return (
        <div className="pointer-events-none" data-inline-readout>
            <div
                ref={valuesRef}
                className="absolute w-max text-foreground-base"
                style={{
                    left: left(widths.values),
                    top: -readoutHeight(rows.length),
                    maxWidth: plot.width
                }}
            >
                {rows.map((row) => (
                    <Text
                        key={row.key}
                        variant="number"
                        render={<div />}
                        className="flex h-8 items-center justify-between gap-2 whitespace-nowrap"
                    >
                        {named && (
                            <span className="inline-flex min-w-0 items-center gap-2">
                                <span
                                    className="size-2 shrink-0"
                                    style={
                                        row.color
                                            ? { backgroundColor: row.color }
                                            : undefined
                                    }
                                    aria-hidden
                                />
                                <span className="truncate">{row.name}</span>
                            </span>
                        )}
                        <span>{row.value ?? "—"}</span>
                    </Text>
                ))}
            </div>
            <Text
                variant="number"
                render={<span ref={dateRef} />}
                className="absolute whitespace-nowrap text-foreground-base"
                style={{
                    left: left(widths.date),
                    top: plot.y + plot.height + 4
                }}
            >
                {date}
            </Text>
        </div>
    );
}
